import React, { useCallback, useMemo } from "react";
import { PopoverPosition } from "@blueprintjs/core";
import classNames from "classnames";
import { get } from "lodash";
import { FieldArray } from "react-final-form-arrays";
import { useSelector } from "react-redux";
import SourceRow from "../SourceRow";
import SourceRowPreview from "../SourceRowPreview";
import Button from "components/Button";
import LearnMoreLink from "rs-components/LearnMoreLink";
import { defaultSource } from "../..";
import { LineGraphElementFormField } from "pages/RS-Workspace/components/Results/components/ElementDialog";
import { HelpId, HELP_SYSTEM_URL } from "shared/helpSystemBindings";
import { isProcessDataViewerSourceCompleted } from "store/rs-workspace/utils/completeness.utils";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { getNextMaxId } from "utils/data";
import { getActiveResultSetIdSelector, hasErroredSourceSelector, } from "store/rs-workspace/selectors/results.selectors";
import PlusIcon from "icons/plus.svg";
import * as styles from "../../styles.module.scss";
export const MAX_VIEWABLE_SOURCES = 6;
const Sources = ({ editMode, form, elementData, restrictions, axesRanges, alternativeFlowsMapping, handleAutoscaleButtonClick, onLockButtonClick, }) => {
    const resultSetId = useSelector(getActiveResultSetIdSelector);
    const hasErroredSource = useSelector(hasErroredSourceSelector({ resultSetId, resultSetElementId: elementData.id }, elementData.type));
    const sources = useMemo(() => elementData.sources ?? [], [elementData]);
    const maxSourcesCount = useMemo(() => getFieldRestrictions(restrictions, [getRequestName(`${LineGraphElementFormField.sources}.Count`)])
        ?.MaxValueEqual ?? 1, [restrictions]);
    const addSourceButtonDisabled = useMemo(() => sources.some((source) => !isProcessDataViewerSourceCompleted(source)) ||
        sources?.length >= maxSourcesCount, [sources, maxSourcesCount]);
    const sourcesRestrictions = useMemo(() => getFieldRestrictions(restrictions, [getRequestName(LineGraphElementFormField.sources)])
        ?.LineGraphSource, [restrictions]);
    const addButtonTitle = useMemo(() => {
        if (!addSourceButtonDisabled) {
            return "Add source";
        }
        if (sources?.length >= maxSourcesCount) {
            return `Maximum number of sources is ${maxSourcesCount}.`;
        }
        return "Complete existing source.";
    }, [addSourceButtonDisabled, sources?.length, maxSourcesCount]);
    const onAddSourceRow = useCallback(() => {
        const currentSources = get(elementData, LineGraphElementFormField.sources) ?? [];
        const maxViewableSourcesReached = currentSources?.filter(({ view }) => view)?.length >= MAX_VIEWABLE_SOURCES;
        form?.mutators.push(LineGraphElementFormField.sources, {
            ...(maxViewableSourcesReached ? { ...defaultSource, view: false } : defaultSource),
            id: getNextMaxId(currentSources),
        });
    }, [form?.mutators, elementData]);
    const onRemoveSource = useCallback((index) => form?.mutators.remove(LineGraphElementFormField.sources, index), [form?.mutators]);
    return (React.createElement("div", { className: classNames(styles.sources, {
            [styles.editMode]: editMode,
            [styles.halfWidth]: !editMode && !elementData.fullView,
        }) },
        React.createElement("div", { className: classNames(styles.header, { [styles.editMode]: editMode }) },
            !editMode && React.createElement("div", { className: classNames(styles.cell, styles.view) }, "View"),
            React.createElement("div", { className: classNames(styles.cell, styles.source) }, "Data source"),
            (editMode || !!elementData.fullView) && (React.createElement("div", { className: classNames(styles.cell, styles.variable) }, "Variable")),
            editMode && React.createElement("div", { className: classNames(styles.cell, styles.unit) }, "Units"),
            (editMode || !!elementData.fullView) && (React.createElement("div", { className: classNames(styles.cell, styles.timing) }, "Timing")),
            React.createElement("div", { className: classNames(styles.cell, styles.line) }, "Line"),
            React.createElement("div", { className: classNames(styles.cell, styles.viewType) }, "Type of view"),
            !editMode && (React.createElement("div", { className: classNames(styles.cell, styles.autoscale) },
                !hasErroredSource && React.createElement("div", { className: styles.spacer }),
                "Autoscale")),
            editMode && (React.createElement(Button, { label: React.createElement(PlusIcon, { width: 25, height: 25 }), square: true, position: PopoverPosition.RIGHT, onClick: onAddSourceRow, showTooltipIfDisabled: true, title: addButtonTitle, classes: {
                    root: classNames(styles.addSource, {
                        [styles.disabled]: addSourceButtonDisabled,
                        [styles.yellow]: !sources?.length,
                    }),
                }, disabled: addSourceButtonDisabled }))),
        React.createElement("div", { className: styles.body }, editMode ? (React.createElement("div", null,
            React.createElement(FieldArray, { name: LineGraphElementFormField.sources }, ({ fields }) => fields.map((name, index) => (React.createElement(SourceRow, { source: fields.value[index], key: name, name: name, index: index, onRemoveSource: () => onRemoveSource(index), restrictions: sourcesRestrictions, elementData: elementData })))),
            React.createElement(LearnMoreLink, { className: styles.linkBlock, url: `${HELP_SYSTEM_URL}#cshid=${HelpId.LINE_GRAPH}` }))) : (sources.map((source, index) => {
            const axisKey = Object.keys(axesRanges ?? {}).find((key) => axesRanges?.[key]?.responseId === source.id);
            const rangeConfig = axesRanges?.[axisKey];
            return (React.createElement(SourceRowPreview, { elementData: elementData, key: source.id, index: index, source: source, sources: sources, axisName: axisKey, rangeConfig: rangeConfig, alternativeFlowsMapping: alternativeFlowsMapping, handleAutoscaleButtonClick: handleAutoscaleButtonClick, onLockButtonClick: onLockButtonClick }));
        })))));
};
export default Sources;
